import axiosInstance from '../../services/axiosInstance';
import { ITemplate } from '../../types/damTypes';
import {
  HealthIssue,
  ImportAnalyzeResult,
  ImportSummary,
  PresentationRecord,
  PresentationRevision,
  PresentationSlide,
  PresentationTemplate,
  PresentationAssetDetail,
  PresentationComment,
  PresentationDeckCategory,
  PresentationGlobalComponent,
  PresentationLibraryLabel,
  PresentationLibrarySlide,
  PresentationStatus,
  SlideObject,
  VisibleAsset,
  VisibleAssetPage,
} from './types';
import { normalizeVisibleAssetPage } from './assetPage';

export async function listPresentations(params?: {
  q?: string;
  deckCategoryId?: string;
  status?: PresentationStatus | 'all';
  pageFormat?: PresentationRecord['pageFormat'];
}): Promise<PresentationRecord[]> {
  const { data } = await axiosInstance.get<PresentationRecord[]>('/api/presentations', {
    params: {
      q: params?.q || undefined,
      deckCategoryId: params?.deckCategoryId || undefined,
      status: params?.status && params.status !== 'all' ? params.status : undefined,
      pageFormat: params?.pageFormat || undefined,
    },
  });
  return data;
}

export async function createPresentation(input: {
  name: string;
  market?: PresentationRecord['market'];
  language?: PresentationRecord['language'];
  deckCategoryId?: string | null;
  templateId?: string;
}): Promise<PresentationRecord> {
  const { data } = await axiosInstance.post<PresentationRecord>('/api/presentations', input);
  return data;
}

export async function getPresentation(id: string): Promise<PresentationRecord> {
  const { data } = await axiosInstance.get<PresentationRecord>(`/api/presentations/${id}`);
  return data;
}

export async function getPresentationCover(id: string, slideId: string, signal?: AbortSignal): Promise<PresentationSlide> {
  const { data } = await axiosInstance.get<PresentationSlide>(`/api/presentations/${id}/slides/${slideId}`, { signal });
  return data;
}

export async function patchPresentation(id: string, patch: Partial<Pick<PresentationRecord, 'name' | 'market' | 'language' | 'deckCategoryId' | 'templateId'>> & { updateMatchingLayouts?: boolean }): Promise<PresentationRecord> {
  const { data } = await axiosInstance.patch<PresentationRecord>(`/api/presentations/${id}`, patch);
  return data;
}

export async function deletePresentation(id: string): Promise<void> {
  await axiosInstance.delete(`/api/presentations/${id}`);
}

export async function duplicatePresentation(id: string): Promise<PresentationRecord> {
  const { data } = await axiosInstance.post<PresentationRecord>(`/api/presentations/${id}/duplicate`);
  return data;
}

export async function createLanguageVersion(
  id: string,
  input: { language: PresentationRecord['language']; market: PresentationRecord['market'] },
): Promise<PresentationRecord> {
  const { data } = await axiosInstance.post<PresentationRecord>(`/api/presentations/${id}/language-versions`, input);
  return data;
}

export async function leaveLanguageGroup(id: string): Promise<PresentationRecord> {
  const { data } = await axiosInstance.post<PresentationRecord>(`/api/presentations/${id}/language-group/leave`);
  return data;
}

export async function copyOriginReview(
  id: string,
  input: { slideId: string; objectId: string },
): Promise<{ slide: PresentationSlide; originReview?: PresentationRecord['originReview'] }> {
  const { data } = await axiosInstance.post<{ slide: PresentationSlide; originReview?: PresentationRecord['originReview'] }>(
    `/api/presentations/${id}/origin-review/copy`,
    input,
  );
  return data;
}

export async function dismissOriginReview(
  id: string,
  input: { slideId: string; objectId: string; signature: string },
): Promise<{ originReview?: PresentationRecord['originReview'] }> {
  const { data } = await axiosInstance.post<{ originReview?: PresentationRecord['originReview'] }>(
    `/api/presentations/${id}/origin-review/dismiss`,
    input,
  );
  return data;
}

export async function insertOriginReviewPage(
  id: string,
  input: { slideId: string },
): Promise<{ slide: PresentationSlide; originReview?: PresentationRecord['originReview'] }> {
  const { data } = await axiosInstance.post<{ slide: PresentationSlide; originReview?: PresentationRecord['originReview'] }>(
    `/api/presentations/${id}/origin-review/insert-page`,
    input,
  );
  return data;
}

export async function addSlide(
  presentationId: string,
  layoutId?: string,
  templateId?: string,
): Promise<PresentationSlide> {
  const { data } = await axiosInstance.post<PresentationSlide>(`/api/presentations/${presentationId}/slides`, {
    layoutId,
    templateId,
  });
  return data;
}

export async function patchSlide(
  presentationId: string,
  slideId: string,
  patch: Partial<{ objects: SlideObject[]; notes: string; order: number; layoutId: string; sectionTitle: string; backgroundColor: string; hidden: boolean; locked: boolean }>
): Promise<PresentationSlide> {
  const { data } = await axiosInstance.patch<PresentationSlide>(`/api/presentations/${presentationId}/slides/${slideId}`, patch);
  return data;
}

export async function deleteSlide(presentationId: string, slideId: string): Promise<void> {
  await axiosInstance.delete(`/api/presentations/${presentationId}/slides/${slideId}`);
}

export async function duplicateSlide(presentationId: string, slideId: string): Promise<PresentationSlide> {
  const { data } = await axiosInstance.post<PresentationSlide>(`/api/presentations/${presentationId}/slides/${slideId}/duplicate`);
  return data;
}

export async function reorderSlides(presentationId: string, slideIds: string[]): Promise<string[]> {
  const { data } = await axiosInstance.post<{ slideIds: string[] }>(`/api/presentations/${presentationId}/slides/reorder`, { slideIds });
  return data.slideIds;
}

export async function runHealthCheck(presentationId: string): Promise<{ readyBlocked: boolean; issues: HealthIssue[]; aiEnabled?: boolean }> {
  const { data } = await axiosInstance.post(`/api/presentations/${presentationId}/health-check`);
  return data;
}

export async function transitionPresentationStatus(id: string, status: PresentationStatus): Promise<PresentationRecord> {
  const { data } = await axiosInstance.post<PresentationRecord>(`/api/presentations/${id}/status`, { status });
  return data;
}

export async function uploadLocalPresentationImage(file: File): Promise<{
  imageUrl: string;
  width?: number;
  height?: number;
  name: string;
}> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await axiosInstance.post('/api/presentations/media/local', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function acceptMediaUpdates(
  presentationId: string,
  items: Array<{ slideId: string; objectId: string; trackingMode?: 'pin' | 'trackLatest' }>
): Promise<PresentationRecord> {
  const { data } = await axiosInstance.post<PresentationRecord>(`/api/presentations/${presentationId}/media/accept`, { items });
  return data;
}

export async function listPresentationComments(presentationId: string, slideId?: string): Promise<PresentationComment[]> {
  const { data } = await axiosInstance.get<PresentationComment[]>(`/api/presentations/${presentationId}/comments`, {
    params: { slideId },
  });
  return data;
}

export async function createPresentationComment(
  presentationId: string,
  input: { slideId: string; body: string; objectId?: string | null }
): Promise<PresentationComment> {
  const { data } = await axiosInstance.post<PresentationComment>(`/api/presentations/${presentationId}/comments`, input);
  return data;
}

export async function resolvePresentationComment(presentationId: string, commentId: string): Promise<PresentationComment> {
  const { data } = await axiosInstance.post<PresentationComment>(`/api/presentations/${presentationId}/comments/${commentId}/resolve`);
  return data;
}

export async function listGlobalComponents(): Promise<PresentationGlobalComponent[]> {
  const { data } = await axiosInstance.get<PresentationGlobalComponent[]>('/api/presentations/global-components');
  return data;
}

export async function createGlobalComponent(input: { name: string; objects: SlideObject[] }): Promise<PresentationGlobalComponent> {
  const { data } = await axiosInstance.post<PresentationGlobalComponent>('/api/presentations/global-components', input);
  return data;
}

export async function deleteGlobalComponent(id: string): Promise<void> {
  await axiosInstance.delete(`/api/presentations/global-components/${id}`);
}

export async function placeGlobalComponent(
  presentationId: string,
  slideId: string,
  componentId: string,
  position?: { x?: number; y?: number }
): Promise<PresentationSlide> {
  const { data } = await axiosInstance.post<PresentationSlide>(
    `/api/presentations/${presentationId}/slides/${slideId}/global-components/${componentId}`,
    position || {}
  );
  return data;
}

export async function detachGlobalComponent(
  presentationId: string,
  slideId: string,
  objectId: string
): Promise<PresentationSlide> {
  const { data } = await axiosInstance.post<PresentationSlide>(
    `/api/presentations/${presentationId}/slides/${slideId}/detach-global`,
    { objectId }
  );
  return data;
}

export async function runPresentationAi(input: {
  presentationId: string;
  slideId: string;
  objectId: string;
  operation: 'rewrite' | 'translate' | 'checkGrammar';
  instruction?: string;
  targetLanguage?: string;
}): Promise<{ text: string; accepted?: boolean; slideId: string; objectId: string }> {
  const { data } = await axiosInstance.post(`/api/presentations/${input.presentationId}/ai`, {
    slideId: input.slideId,
    objectId: input.objectId,
    operation: input.operation,
    instruction: input.instruction,
    targetLanguage: input.targetLanguage,
  });
  return data;
}

export async function runPresentationChat(input: {
  presentationId: string;
  message: string;
  history?: Array<{ role: 'user' | 'assistant'; content: string }>;
}): Promise<{ text: string }> {
  const { data } = await axiosInstance.post(`/api/presentations/${input.presentationId}/ai/chat`, {
    message: input.message,
    history: input.history,
  });
  return data;
}

export async function exportPresentationPdf(
  presentationId: string,
  input?: { pages?: string[]; slideIds?: string[] } | string[],
): Promise<{ publicUrl: string; bytes: number }> {
  const body = Array.isArray(input) ? { pages: input } : (input || {});
  const { data } = await axiosInstance.post(`/api/presentations/${presentationId}/export/pdf`, body, {
    timeout: 180000,
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
  });
  return data;
}

export function pdfDownloadName(presentationName?: string): string {
  const safe = String(presentationName || 'presentation').replace(/[<>:"/\\|?*]+/g, ' ').trim();
  return `${safe || 'presentation'}.pdf`;
}

export async function openExportedPdf(publicUrl: string, filename: string): Promise<string> {
  const { data } = await axiosInstance.get(publicUrl, { responseType: 'blob', timeout: 60000 });
  const objectUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  return objectUrl;
}

export async function analyzePresentationImport(
  presentationId: string,
  kind: 'pptx' | 'pdf',
  file: File
): Promise<ImportAnalyzeResult> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await axiosInstance.post<ImportAnalyzeResult>(
    `/api/presentations/${presentationId}/import/${kind}/analyze`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 10 * 60 * 1000,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    }
  );
  return data;
}

export async function commitPresentationImport(
  presentationId: string,
  kind: 'pptx' | 'pdf',
  sessionId: string,
  colorMap: Record<string, string>
): Promise<ImportSummary> {
  const { data } = await axiosInstance.post<ImportSummary>(
    `/api/presentations/${presentationId}/import/${kind}/commit`,
    { sessionId, colorMap },
    { timeout: 10 * 60 * 1000 }
  );
  return data;
}

export async function cancelPresentationImport(presentationId: string, sessionId: string): Promise<void> {
  await axiosInstance.delete(`/api/presentations/${presentationId}/import/sessions/${sessionId}`);
}

function asDeckCategory(item: PresentationDeckCategory): PresentationDeckCategory {
  const parent = item.parentCategory as unknown;
  return {
    _id: String(item._id),
    name: item.name,
    order: Number(item.order) || 0,
    kind: item.kind === 'salesSheet' ? 'salesSheet' : 'presentation',
    parentCategory: !parent ? null : typeof parent === 'object' && parent && '_id' in parent
      ? String((parent as { _id: unknown })._id)
      : String(parent),
  };
}

export async function listDeckCategories(kind?: PresentationDeckCategory['kind']): Promise<PresentationDeckCategory[]> {
  const { data } = await axiosInstance.get<PresentationDeckCategory[]>('/api/presentations/deck-categories', {
    params: { kind: kind || 'presentation' },
  });
  return (data || []).map(asDeckCategory);
}

export async function createDeckCategory(input: {
  name: string;
  parentCategory?: string | null;
  kind?: PresentationDeckCategory['kind'];
}): Promise<PresentationDeckCategory> {
  const { data } = await axiosInstance.post<PresentationDeckCategory>('/api/presentations/deck-categories', input);
  return data;
}

export async function patchDeckCategory(id: string, patch: { name?: string; order?: number }): Promise<PresentationDeckCategory> {
  const { data } = await axiosInstance.patch<PresentationDeckCategory>(`/api/presentations/deck-categories/${id}`, patch);
  return data;
}

export async function deleteDeckCategory(id: string): Promise<void> {
  await axiosInstance.delete(`/api/presentations/deck-categories/${id}`);
}

export async function listPresentationCategories(): Promise<Array<{
  _id: string;
  name: string;
  parentCategory?: string | { _id: string; name: string } | null;
}>> {
  const { data } = await axiosInstance.get('/api/presentations/categories');
  return data;
}

export async function searchPresentationAssets(
  query: string,
  categoryId?: string,
  page?: { skip?: number; limit?: number; sort?: string },
  signal?: AbortSignal,
): Promise<VisibleAssetPage> {
  const { data } = await axiosInstance.get<VisibleAssetPage | VisibleAsset[]>('/api/presentations/assets', {
    params: {
      q: query,
      categoryId: categoryId || undefined,
      skip: page?.skip,
      limit: page?.limit,
      sort: page?.sort,
    },
    signal,
  });
  return normalizeVisibleAssetPage(data, page);
}

export async function listDamTemplates(): Promise<ITemplate[]> {
  const { data } = await axiosInstance.get<ITemplate[]>('/api/templates');
  return Array.isArray(data) ? data : [];
}

export async function getPresentationAsset(assetId: string): Promise<PresentationAssetDetail> {
  const { data } = await axiosInstance.get<PresentationAssetDetail>(`/api/presentations/assets/${assetId}`);
  return data;
}

export async function placePresentationAsset(assetId: string, versionId?: string | null): Promise<{
  imageUrl: string;
  usedClippingPath: boolean;
  previewUrl?: string;
  width?: number;
  height?: number;
  assetId: string;
  assetVersionId: string;
}> {
  const { data } = await axiosInstance.post(`/api/presentations/assets/${assetId}/place`, { versionId });
  return data;
}

export async function listTemplates(options?: { includeArchived?: boolean }): Promise<PresentationTemplate[]> {
  const { data } = await axiosInstance.get<PresentationTemplate[]>('/api/presentations/templates', {
    params: options?.includeArchived ? { includeArchived: '1' } : undefined,
  });
  return data;
}

export async function getTemplate(templateId: string): Promise<PresentationTemplate> {
  const { data } = await axiosInstance.get<PresentationTemplate>(`/api/presentations/templates/${templateId}`);
  return data;
}

export async function createTemplate(input: { name: string; pageFormat?: PresentationTemplate['pageFormat'] }): Promise<PresentationTemplate> {
  const { data } = await axiosInstance.post<PresentationTemplate>('/api/presentations/templates', input);
  return data;
}

export async function patchTemplate(templateId: string, patch: { name?: string; isActive?: boolean; pageFormat?: PresentationTemplate['pageFormat'] }): Promise<PresentationTemplate> {
  const { data } = await axiosInstance.patch<PresentationTemplate>(`/api/presentations/templates/${templateId}`, patch);
  return data;
}

export async function duplicateTemplate(templateId: string): Promise<PresentationTemplate> {
  const { data } = await axiosInstance.post<PresentationTemplate>(`/api/presentations/templates/${templateId}/duplicate`);
  return data;
}

export async function addTemplateLayout(templateId: string, input?: {
  name?: string;
  objects?: SlideObject[];
  backgroundColor?: string;
  sourcePageFormat?: string | null;
}): Promise<{ layoutId: string; name: string; objects: SlideObject[]; backgroundColor?: string }> {
  const { data } = await axiosInstance.post(`/api/presentations/templates/${templateId}/layouts`, input || {});
  return data;
}

export async function copyTemplateLayout(
  sourceTemplateId: string,
  sourceLayoutId: string,
  input: { targetTemplateId: string; name?: string; removeSource?: boolean },
): Promise<{ layoutId: string; name: string; objects: SlideObject[]; backgroundColor?: string }> {
  const { data } = await axiosInstance.post(
    `/api/presentations/templates/${sourceTemplateId}/layouts/${sourceLayoutId}/copy`,
    input,
  );
  return data;
}

export async function patchTemplateLayout(
  templateId: string,
  layoutId: string,
  patch: { name?: string; objects?: SlideObject[]; backgroundColor?: string }
): Promise<{ layoutId: string; name: string; objects: SlideObject[]; backgroundColor?: string }> {
  const { data } = await axiosInstance.patch(`/api/presentations/templates/${templateId}/layouts/${layoutId}`, patch);
  return data;
}

export async function deleteTemplateLayout(templateId: string, layoutId: string): Promise<void> {
  await axiosInstance.delete(`/api/presentations/templates/${templateId}/layouts/${layoutId}`);
}

export async function listLibrarySlides(options?: { includeArchived?: boolean }): Promise<PresentationLibrarySlide[]> {
  const { data } = await axiosInstance.get<PresentationLibrarySlide[]>('/api/presentations/library-slides', {
    params: options?.includeArchived ? { includeArchived: '1' } : undefined,
  });
  return data;
}

export async function getLibrarySlide(id: string): Promise<PresentationLibrarySlide> {
  const { data } = await axiosInstance.get<PresentationLibrarySlide>(`/api/presentations/library-slides/${id}`);
  return data;
}

export async function createLibrarySlide(input: {
  name: string;
  pageFormat?: PresentationLibrarySlide['pageFormat'];
  backgroundColor?: string;
  objects?: SlideObject[];
  group?: PresentationLibrarySlide['group'];
  labelIds?: string[];
}): Promise<PresentationLibrarySlide> {
  const { data } = await axiosInstance.post<PresentationLibrarySlide>('/api/presentations/library-slides', input);
  return data;
}

export async function patchLibrarySlide(id: string, patch: {
  name?: string;
  isActive?: boolean;
  pageFormat?: PresentationLibrarySlide['pageFormat'];
  backgroundColor?: string;
  objects?: SlideObject[];
  group?: PresentationLibrarySlide['group'] | null;
  labelIds?: string[];
}): Promise<PresentationLibrarySlide> {
  const { data } = await axiosInstance.patch<PresentationLibrarySlide>(`/api/presentations/library-slides/${id}`, patch);
  return data;
}

export async function listLibraryLabels(): Promise<PresentationLibraryLabel[]> {
  const { data } = await axiosInstance.get<PresentationLibraryLabel[]>('/api/presentations/library-labels');
  return data;
}

export async function createLibraryLabel(name: string): Promise<PresentationLibraryLabel> {
  const { data } = await axiosInstance.post<PresentationLibraryLabel>('/api/presentations/library-labels', { name });
  return data;
}

export async function patchLibraryLabel(id: string, patch: { name: string }): Promise<PresentationLibraryLabel> {
  const { data } = await axiosInstance.patch<PresentationLibraryLabel>(`/api/presentations/library-labels/${id}`, patch);
  return data;
}

export async function deleteLibraryLabel(id: string): Promise<void> {
  await axiosInstance.delete(`/api/presentations/library-labels/${id}`);
}

export async function addSlideFromLibrary(
  presentationId: string,
  librarySlideId: string,
  options?: { follow?: boolean },
): Promise<PresentationSlide> {
  const { data } = await axiosInstance.post<PresentationSlide>(
    `/api/presentations/${presentationId}/slides/from-library`,
    { librarySlideId, follow: options?.follow !== false },
  );
  return data;
}

export async function detachLibrarySlide(presentationId: string, slideId: string): Promise<PresentationSlide> {
  const { data } = await axiosInstance.post<PresentationSlide>(
    `/api/presentations/${presentationId}/slides/${slideId}/detach-library`,
  );
  return data;
}

export async function applySlideLayout(presentationId: string, slideId: string, layoutId?: string): Promise<PresentationSlide> {
  const { data } = await axiosInstance.post<PresentationSlide>(
    `/api/presentations/${presentationId}/slides/${slideId}/apply-layout`,
    { layoutId }
  );
  return data;
}

export async function listRevisions(presentationId: string): Promise<PresentationRevision[]> {
  const { data } = await axiosInstance.get<PresentationRevision[]>(`/api/presentations/${presentationId}/revisions`);
  return data;
}

export async function createSnapshot(presentationId: string): Promise<{ revision: number }> {
  const { data } = await axiosInstance.post(`/api/presentations/${presentationId}/revisions/snapshot`);
  return data;
}

export async function restoreRevision(presentationId: string, revision: number): Promise<PresentationRecord> {
  const { data } = await axiosInstance.post<PresentationRecord>(`/api/presentations/${presentationId}/revisions/restore`, { revision });
  return data;
}

export async function fetchPresentationSettings(): Promise<{
  settings: import('./presentationSettings').PresentationSettingsState;
  factory?: import('./presentationSettings').PresentationSettingsState;
}> {
  const { data } = await axiosInstance.get('/api/presentations/settings');
  return data;
}

export async function savePresentationSettings(
  patch: Partial<import('./presentationSettings').PresentationSettingsState>,
): Promise<{ settings: import('./presentationSettings').PresentationSettingsState }> {
  const { data } = await axiosInstance.put('/api/presentations/settings', patch);
  return data;
}
